import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  Coins,
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  Repeat,
  CalendarClock,
  Building2,
} from 'lucide-react';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import Pagination from '@/components/ui/Pagination';
import SearchInputDebounced from '@/components/ui/SearchInputDebounced';
import { DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce';
import { creditPlanService } from '@/services/credit-plan.service';
import { ADMIN_CREDIT_PLAN_CREATE, ADMIN_CREDIT_PLANS, ADMIN_TENANT_CREDIT_PLANS } from '@/constants/constant-routes';
import type { CreditPlan } from '@/types/credit-plan.types';

const ITEMS_PER_PAGE = 4;

export default function CreditPlans() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [planToDelete, setPlanToDelete] = useState<CreditPlan | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  // Server-side search — the API filters by name/description (searchTerm is in the query key).
  const { data: listResp, isLoading } = useQuery({
    queryKey: ['credit-plans', searchTerm],
    queryFn: () => creditPlanService.list(searchTerm),
  });

  const filteredPlans: CreditPlan[] = useMemo(() => {
    const raw = (listResp as any)?.data;
    return Array.isArray(raw) ? raw : [];
  }, [listResp]);

  const hasActiveSearch = searchTerm.trim().length > 0;

  // Pagination — fixed 4 per page over the filtered set. Clamp so deletes/search never strand an empty page.
  const totalPages = Math.max(1, Math.ceil(filteredPlans.length / ITEMS_PER_PAGE));
  const page = Math.min(currentPage, totalPages);
  const pagedPlans = useMemo(
    () => filteredPlans.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE),
    [filteredPlans, page],
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['credit-plans'] });
  const errMsg = (error: any, fallback: string) =>
    error?.response?.data?.error?.error_description ||
    error?.response?.data?.error?.message ||
    error?.message ||
    fallback;

  const goCreate = () => navigate(ADMIN_CREDIT_PLAN_CREATE);
  const goAssign = () => navigate(ADMIN_TENANT_CREDIT_PLANS);
  const goEdit = (plan: CreditPlan) => navigate(`${ADMIN_CREDIT_PLANS}/${plan.plan_key}/edit`);

  const deleteMutation = useMutation({
    mutationFn: (plan_key: string) => creditPlanService.remove(plan_key),
    onSuccess: () => { toast.success('Credit plan deleted.'); invalidate(); setPlanToDelete(null); },
    onError: (error: any) => toast.error(errMsg(error, 'Failed to delete plan.')),
  });

  return (
    <div className="space-y-6">
      {/* Header — violet/purple theme */}
      <div className="bg-gradient-to-r from-brand-violet-600 to-brand-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg"><Coins className="w-7 h-7" /></div>
            <div>
              <h1 className="text-2xl font-bold">Credit Plans</h1>
              <p className="text-brand-violet-100">Define the credit allotment and validity for generation.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={goAssign}
              className="flex items-center gap-2 px-4 py-2 bg-white/15 text-white rounded-lg hover:bg-white/25 ring-1 ring-white/30 transition-colors font-semibold"
            >
              <Building2 className="w-5 h-5" />
              <span className="hidden sm:inline">Assign Plan</span>
            </button>
            <button
              onClick={goCreate}
              className="flex items-center gap-2 px-4 py-2 bg-white text-brand-violet-700 rounded-lg hover:bg-brand-violet-50 transition-colors font-semibold shadow-lg"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">Create Plan</span>
            </button>
          </div>
        </div>
      </div>

      {/* Search — stays visible on a zero-result search so it can be cleared. */}
      {(filteredPlans.length > 0 || hasActiveSearch) && (
        <div className="card">
          <SearchInputDebounced
            value={searchTerm}
            onChange={(val) => { setSearchTerm(val); setCurrentPage(1); }}
            placeholder="Search by name or description…"
            debounceDelay={DEFAULT_DEBOUNCE_DELAY}
            className="w-full"
          />
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="card">
              <SkeletonLoader type="title" className="h-6 w-48 mb-3" />
              <SkeletonLoader type="text" className="h-4 w-full mb-2" />
              <SkeletonLoader type="text" className="h-4 w-2/3" />
            </div>
          ))}
        </div>
      ) : filteredPlans.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-brand-violet-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Coins className="w-8 h-8 text-brand-violet-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {hasActiveSearch ? 'No plans match your search' : 'No credit plans yet'}
          </h3>
          <p className="text-gray-600 mb-6">
            {hasActiveSearch ? 'Try a different search term or clear the filter.' : 'Create your first plan to define credits and validity.'}
          </p>
          {!hasActiveSearch && (
            <button
              onClick={goCreate}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-brand-violet-600 to-brand-purple-600 text-white rounded-lg hover:from-brand-violet-700 hover:to-brand-purple-700 transition-all font-semibold"
            >
              <Plus className="w-5 h-5" />
              <span>Create Plan</span>
            </button>
          )}
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {pagedPlans.map((plan) => (
            <div
              key={plan._id}
              className="bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg hover:border-brand-violet-300 transition-all"
            >
              <div className="p-5">
                <h3 className="text-lg font-semibold text-gray-900 truncate mb-3">{plan.plan_name}</h3>

                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{plan.description}</p>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-brand-violet-50 rounded-lg p-3">
                    <div className="flex items-center gap-1.5 text-brand-violet-700">
                      <Coins className="w-4 h-4" />
                      <span className="text-xs font-medium">Credits</span>
                    </div>
                    <p className="text-xl font-bold text-gray-900 mt-1">{plan.credits.toLocaleString()}</p>
                  </div>
                  <div className="bg-brand-blue-50 rounded-lg p-3">
                    <div className="flex items-center gap-1.5 text-brand-blue-700">
                      <CalendarClock className="w-4 h-4" />
                      <span className="text-xs font-medium">Validity</span>
                    </div>
                    <p className="text-xl font-bold text-gray-900 mt-1">{plan.validity_days} days</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${plan.auto_renew ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}>
                    <Repeat className="w-3 h-3" /> Auto-renew {plan.auto_renew ? 'on' : 'off'}
                  </span>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${plan.rollover_unused_credits ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-500'}`}>
                    <RefreshCw className="w-3 h-3" /> Rollover {plan.rollover_unused_credits ? 'on' : 'off'}
                  </span>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <span className="text-xs text-gray-400">
                    Updated {(() => { try { return format(new Date(plan.updatedAt), 'PP'); } catch { return '—'; } })()}
                  </span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => goEdit(plan)} className="p-2 text-brand-violet-600 hover:bg-brand-violet-50 rounded-lg" title="Edit">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => setPlanToDelete(plan)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {filteredPlans.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={filteredPlans.length}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setCurrentPage}
              showItemsPerPage={false}
            />
          </div>
        )}
        </>
      )}

      {/* Delete confirmation */}
      <DeleteConfirmationModal
        isOpen={!!planToDelete}
        onClose={() => setPlanToDelete(null)}
        onConfirm={() => planToDelete && deleteMutation.mutate(planToDelete.plan_key)}
        title="Delete Credit Plan"
        description="Are you sure you want to delete this credit plan?"
        itemName={planToDelete?.plan_name}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
